import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X, Lightbulb } from "lucide-react";
import { findHelp } from "@/constants/pageHelp";
import { useAuth } from "@/context/AuthContext";

export function HelpButton() {
  const loc = useLocation();
  const { employee } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (!employee) return null;
  if (loc.pathname.startsWith("/kiosk")) return null;
  if (loc.pathname.startsWith("/login") || loc.pathname.startsWith("/register") || loc.pathname.startsWith("/welcome") || loc.pathname.startsWith("/onboarding")) return null;
  if (employee.role === "kiosk") return null;

  const help = findHelp(loc.pathname);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="What is this page?"
        aria-label="Help"
        className="flex h-9 w-9 items-center justify-center rounded-full text-gray2 hover:bg-offwhite hover:text-teal-dark no-print transition"
      >
        <HelpCircle size={20} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-ink/60 p-4 md:items-center md:justify-center" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-soft ring-1 ring-line md:max-w-lg"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-mint text-teal-dark">
                  <HelpCircle size={18} />
                </span>
                <h2 className="text-lg font-bold">{help?.title ?? "About this page"}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray2 hover:text-charcoal" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {help ? (
              <>
                <p className="text-sm leading-relaxed text-charcoal whitespace-pre-line">{help.body}</p>
                {help.tips && help.tips.length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {help.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-lg bg-offwhite p-2.5 text-xs">
                        <Lightbulb size={14} className="mt-0.5 shrink-0 text-warn" />
                        <span className="text-charcoal">{tip}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray2">
                No specific help written for this page yet. If you're stuck, click <strong>Report a bug</strong> in the bottom-right and let us know what you were trying to do.
              </p>
            )}

            <div className="mt-4 border-t border-line pt-3 text-xs text-gray2">
              Need more help? Ask your manager in <strong>Chat</strong>, or use the floating <strong>Report a bug</strong> button (bottom-right).
            </div>
          </div>
        </div>
      )}
    </>
  );
}
